import { Divider, Stack, Tooltip, Typography, useTheme } from "@mui/material"
import { type ReactNode, useMemo } from "react"
import { isDefined, pluralize } from "../../../utils"
import { AchievementBadge, DiscountChip } from "../../dataDisplay"
import { CardBottomColorLayout } from "../Card"

export type StockCardProps = {
  index: number
  formattedValueHt: string
  nbDaysValueTtc?: number | null
  vatRate?: string | null
  norm?: { minDays: number; maxDays: number } | null
  uppStockAverage?: number | null
}

export const StockCard = (props: StockCardProps) => {
  const { palette } = useTheme()

  const colors = useMemo(
    (): string[] => [
      palette.common.kawaiiBlue,
      palette.common.lightCyan,
      palette.common.lavender,
      palette.common.lightPink,
      palette.common.blush,
    ],
    [palette],
  )

  return (
    <CardBottomColorLayout
      bottomColor={colors[props.index]}
      sx={{ gap: 1, color: palette.primary.dark1 }}
    >
      {/* HEADER */}
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack gap={1} direction="row" alignItems="inherit">
          <DiscountChip
            label={props.vatRate ? `${props.vatRate} %` : "-"}
            fontSize={20}
            variant="small"
          />
          <Typography variant="14px" fontWeight={500}>
            TVA
          </Typography>
        </Stack>

        <NormTooltip {...props} />
      </Stack>

      {/* CONTENT */}
      <Stack my={3}>
        <Typography fontSize="36px" fontWeight={600}>
          {props.nbDaysValueTtc ?? "-"}
        </Typography>
        <Typography variant="12px" fontWeight={500}>
          {pluralize(props.nbDaysValueTtc ?? 0, "jour", "jours")} de stock
        </Typography>
        {/* En attente IPPON pour nouveau calcul de la moyenne */}
        {/*<Typography
          variant="12px"
          fontWeight={500}
          sx={{ color: palette.primary.dark4 }}
        >
          Moyenne UPP{" "}
          <Typography component="span" fontWeight={600}>
            ·{" "}
            {isDefined(props.uppStockAverage) ? `${props.uppStockAverage} j` : "--"}
          </Typography>
        </Typography>*/}
      </Stack>

      <Divider />

      {/* BOTTOM */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        pt={2}
      >
        <Typography variant="12px" fontWeight={500}>
          Valeur
        </Typography>
        <Typography variant="12px" fontWeight={600}>
          {props.formattedValueHt !== "" ? props.formattedValueHt : "-"}
        </Typography>
      </Stack>
    </CardBottomColorLayout>
  )
}

const NormTooltip = (props: StockCardProps) => {
  const Norm = useMemo(() => {
    if (!isDefined(props.nbDaysValueTtc) || !props.norm) return null

    const isAchieved =
      props.nbDaysValueTtc >= props.norm.minDays &&
      props.nbDaysValueTtc <= props.norm.maxDays

    return <AchievementBadge isAchieved={isAchieved} />
  }, [props.nbDaysValueTtc, props.norm])

  if (!Norm) return null

  return (
    <Tooltip
      placement="bottom-start"
      arrow={false}
      title={<TooltipTitle norm={props.norm} Icon={Norm} />}
    >
      {Norm}
    </Tooltip>
  )
}

const TooltipTitle = ({
  norm,
  Icon,
}: {
  norm: StockCardProps["norm"]
  Icon: ReactNode
}) => (
  <Stack direction="row" gap={2} p={1.5} alignItems="center">
    <Stack>
      <Typography variant="12px">Norme suggérée</Typography>
      <Typography variant="12px" fontWeight={600}>
        Entre {norm?.minDays} et {norm?.maxDays} jours
      </Typography>
    </Stack>
    {Icon}
  </Stack>
)
